import React, {useState, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Form, Formik} from 'formik';
import {useHistory} from 'react-router-dom';
import {Checkbox, FormControlLabel} from '@material-ui/core';
import DashboardContent from 'components/DashboardContent/styles';
import DashboardHeader from 'components/DashboardHeader/styles';
import Headline from 'components/Headline/styles';
import {
  SignUpCheckboxContainer,
  SignUpContainer,
  SignUpError,
  SignUpSubtitle,
  SignUpText,
  SignUpTitle,
  SignUpSignatureContainer,
  PromoCodeContainer,
  PromoCodeButtonWrapper
} from 'routes/App/byocOnboarding/SignUp/styles';
import {ByocOnboardingLoadingOverlay} from 'routes/App/byocOnboarding/styles';
import ByocProgressBar from 'components/ByocProgressBar';
import IconSelfDeliverySignUpBasic from 'assets/IconSelfDeliverySignUpBasic';
import IconSelfDeliverySignUpProf from 'assets/IconSelfDeliverySignUpProf';
import IconSelfDeliverySignUpOther from 'assets/IconSelfDeliverySignUpOther';
import IconSelfDeliverySignUpEnterprise from 'assets/IconSelfDeliverySignUpEnterprise';
import {ButtonOutline, ButtonPrimary} from 'components/buttons';
import DialogAction from 'components/DialogAction/styles';
import DialogActions from 'components/DialogActions/styles';
import SelfDeliverySignUpSchema from 'helpers/services/formValidationSchemas/SelfDeliverySignUpSchema';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import SignaturePad from 'components/SignaturePad';
import {PROGRESS_BAR_STEPS} from 'helpers/constants/byocOnboarding';
import {FieldLabel} from 'components/FieldLabel/styles';
import {DashboardField} from 'routes/App/dashboard/styles';
import FieldInput from 'components/FieldInput';
import {
  UPDATE_BYOC_ONBOARDING_STEP,
  SAVE_BYOC_SIGNATURE
} from 'store/byocOnboarding/actions';
import Loading from 'components/shared/Utils/Loading';
import {scrollToFirstError} from 'helpers/functionUtils/scrollToFirstError';
import {EmailMarketingSpinner} from 'routes/App/emailMarketing/components/styles';

const SignUp = () => {
  const PLANS = [
    {
      id: 'flat_rate',
      title: 'Flat Rate',
      description: 'Perfect for getting started'
    },
    {
      id: 'menu_markup',
      title: 'Item Markup',
      description: 'Perfect for existing restaurants'
    }
  ];

  const history = useHistory();
  const dispatch = useDispatch();
  const {data, loading, globalLoading} = useSelector(
    (state) => state.byocOnboardingReducer || {}
  );
  const signupData = data?.signup || data?.Signup || {};

  const [discount, setDiscount] = useState(49);
  const [promoError, setPromoError] = useState('');

  // Calculate discount from saved promo code
  useEffect(() => {
    const promoCodeValue = signupData?.promocode || signupData?.Promocode;
    if (promoCodeValue) {
      const promoCode = promoCodeValue.trim().toUpperCase();
      if (promoCode === 'NOY0') {
        setDiscount(0);
      } else if (promoCode === 'NOY20') {
        setDiscount(20);
      } else {
        setDiscount(49);
      }
    }
  }, [signupData?.promocode, signupData?.Promocode]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <DashboardContent className='DashboardContent'>
      <DashboardHeader>
        <Headline className='mTop5'>Self Delivery</Headline>
      </DashboardHeader>
      {globalLoading ? (
        <EmailMarketingSpinner>
          <Loading />
        </EmailMarketingSpinner>
      ) : (
        <>
          <ByocProgressBar currentStep={1} steps={PROGRESS_BAR_STEPS} />
          <Formik
            initialValues={{
              plan: signupData?.plan || signupData?.Plan || '',
              isAgreed:
                signupData?.termsAgreed || signupData?.TermsAgreed || false,
              // Signature is now handled via hasSignature flag in backend
              // We still need signature in form for validation, but it's not stored in backend
              signature: signupData?.hasSignature ? 'signed' : '',
              promoCode: signupData?.promocode || signupData?.Promocode || ''
            }}
            enableReinitialize
            validationSchema={SelfDeliverySignUpSchema}
            onSubmit={(values) => {
              const promoCode = values.promoCode?.trim() || null;
              // hasSignature is true if signature exists (not empty and not 'signed' placeholder)
              const hasSignature =
                values.signature &&
                values.signature !== '' &&
                values.signature !== 'signed';

              dispatch(
                UPDATE_BYOC_ONBOARDING_STEP({
                  signup: {
                    plan: values.plan,
                    promocode: promoCode,
                    termsAgreed: values.isAgreed,
                    hasSignature,
                    isFinished: true
                  },
                  successCallback: () => {
                    // Save signature separately if it exists
                    if (hasSignature && values.signature) {
                      dispatch(
                        SAVE_BYOC_SIGNATURE({
                          signature: values.signature,
                          successCallback: () => {
                            history.push('/byoc-onboarding/sign-up-success');
                          }
                        })
                      );
                    } else {
                      history.push('/byoc-onboarding/sign-up-success');
                    }
                  }
                })
              );
            }}
          >
            {({
              values,
              setFieldValue,
              submitForm,
              errors,
              touched,
              handleChange,
              setTouched,
              handleSubmit,
              validateForm
            }) => {
              // Scroll to first error when validation fails after submit attempt
              // Only scroll if all fields are touched (indicating a submit attempt)
              useEffect(() => {
                const allFieldsTouched = Object.keys(values).every(
                  (key) => touched[key]
                );
                const hasErrors = Object.keys(errors).some(
                  (key) => errors[key] && touched[key]
                );

                if (hasErrors && allFieldsTouched) {
                  // Use longer delay to ensure DOM is updated
                  scrollToFirstError({errors, touched, delay: 300});
                }
              }, [errors, touched, values]);

              const handleSubmitWithScroll = async (e) => {
                e?.preventDefault();

                // Touch all fields to trigger validation
                const fieldsToTouch = Object.keys(values);
                const touchedFields = {};
                fieldsToTouch.forEach((field) => {
                  touchedFields[field] = true;
                });
                setTouched(touchedFields, false);

                // Validate form and check for errors
                const validationErrors = await validateForm();

                // Wait a bit for Formik to update errors state
                setTimeout(() => {
                  const currentErrors = validationErrors || errors;
                  const hasErrors =
                    currentErrors && Object.keys(currentErrors).length > 0;

                  if (hasErrors) {
                    // If there are errors, scroll to first one
                    scrollToFirstError({
                      errors: currentErrors,
                      touched: touchedFields,
                      delay: 100
                    });
                  } else {
                    // If no errors, proceed with submit
                    handleSubmit(e);
                  }
                }, 100);
              };

              const handlePromoCodeChange = (e) => {
                handleChange(e);
                const promoCodeValue = e.target.value?.trim();

                // If field is empty, clear error and reset price to original
                if (!promoCodeValue) {
                  setPromoError('');
                  setDiscount(49);
                }
              };

              const handleApplyPromo = () => {
                const promoCode = values.promoCode?.trim().toUpperCase();
                setPromoError('');

                if (!promoCode) {
                  setDiscount(49);
                  return;
                }

                if (promoCode === 'NOY0') {
                  setDiscount(0);
                  setPromoError('');
                } else if (promoCode === 'NOY20') {
                  setDiscount(20);
                  setPromoError('');
                } else {
                  setDiscount(49);
                  setPromoError('Invalid promo code');
                }
              };

              return (
                <Form onSubmit={handleSubmitWithScroll}>
                  <SignUpContainer>
                    <SignUpTitle>Sign Up</SignUpTitle>
                    <SignUpSubtitle>
                      <span>*</span> Plan Selection
                    </SignUpSubtitle>

                    <div className='plan-grid'>
                      {PLANS.map((plan) => {
                        const isActive = values.plan === plan.id;

                        return (
                          <button
                            key={plan.id}
                            type='button'
                            className={`plan-card ${
                              isActive ? 'plan-card--active' : ''
                            }`}
                            onClick={() => setFieldValue('plan', plan.id)}
                          >
                            {plan.icon}
                            <div className='plan-card__title'>{plan.title}</div>
                            <div className='plan-card__description'>
                              {plan.description}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {errors.plan && touched.plan && (
                      <SignUpError>
                        <ErrorSignWrapper className='SignUpError'>
                          <ErrorSign>{errors.plan}</ErrorSign>
                        </ErrorSignWrapper>
                      </SignUpError>
                    )}
                    <SignUpText className='mTop35'>
                      <span className='gray'>
                        * Integration fee is{' '}
                        {discount !== 49 ? (
                          <>
                            <span
                              className='bold'
                              style={{textDecoration: 'line-through'}}
                            >
                              $49
                            </span>{' '}
                            <span className='bold'>${discount}</span>
                          </>
                        ) : (
                          <span className='bold'>${discount}</span>
                        )}{' '}
                        a month
                      </span>
                    </SignUpText>

                    <PromoCodeContainer className='mTop20'>
                      <DashboardField className='wAutoSpace paddingL0 paddingB0'>
                        <FieldLabel className='FieldLabelRegular'>
                          Promo Code
                        </FieldLabel>
                        <PromoCodeButtonWrapper>
                          <FieldInput
                            value={values?.promoCode}
                            onChange={handlePromoCodeChange}
                            name='promoCode'
                          />
                          <ButtonPrimary
                            onClick={handleApplyPromo}
                            type='button'
                            style={{marginLeft: '10px', minWidth: '80px'}}
                          >
                            <span>Apply</span>
                          </ButtonPrimary>
                        </PromoCodeButtonWrapper>
                        {promoError && (
                          <SignUpError>
                            <ErrorSignWrapper className='SignUpError'>
                              <ErrorSign>{promoError}</ErrorSign>
                            </ErrorSignWrapper>
                          </SignUpError>
                        )}
                        {errors.promoCode && touched.promoCode && !promoError && (
                          <SignUpError>
                            <ErrorSignWrapper className='SignUpError'>
                              <ErrorSign>{errors.promoCode}</ErrorSign>
                            </ErrorSignWrapper>
                          </SignUpError>
                        )}
                      </DashboardField>
                    </PromoCodeContainer>

                    <SignUpSubtitle className='mTop32'>
                      Service Agreement
                    </SignUpSubtitle>
                    <SignUpText className='bold mTop10'>
                      By selecting a plan and proceeding, you agree to the
                      following terms:
                    </SignUpText>
                    <SignUpText>
                      The following features are not supported through the Self
                      Delivery integration on Uber Eats, Grubhub, Doordash etc:
                      Catering, Multi-pickup, etc.
                    </SignUpText>
                    <SignUpCheckboxContainer>
                      <FormControlLabel
                        className='Say2EatCheckbox'
                        control={
                          <Checkbox
                            labelStyle={{display: 'none'}}
                            iconStyle={{fill: '#FF2D38'}}
                            inputStyle={{color: '#FF2D38'}}
                            style={{
                              color: '#FF2D38'
                            }}
                            onChange={(e) => {
                              setFieldValue('isAgreed', e.target.checked);
                            }}
                            checked={values.isAgreed}
                          />
                        }
                      />
                      <div className='text'>
                        <span>*</span> I agree to the{' '}
                        <a
                          href='https://www.getsauce.com/termsandconditions'
                          target='_blank'
                          rel='noreferrer'
                        >
                          service agreement and terms
                        </a>
                      </div>
                    </SignUpCheckboxContainer>
                    {errors.isAgreed && touched.isAgreed && (
                      <SignUpError>
                        <ErrorSignWrapper className='SignUpError'>
                          <ErrorSign>{errors.isAgreed}</ErrorSign>
                        </ErrorSignWrapper>
                      </SignUpError>
                    )}
                    <SignUpText className='mTop35'>
                      <span>*</span> Signature{' '}
                      <span className='gray'>(Draw your signature below)</span>
                    </SignUpText>
                    <SignUpSignatureContainer>
                      <SignaturePad
                        value={values.signature}
                        onChange={(signatureData) => {
                          setFieldValue('signature', signatureData);
                        }}
                        width='100%'
                        height={100}
                      />
                    </SignUpSignatureContainer>
                    {errors.signature && touched.signature && (
                      <SignUpError>
                        <ErrorSignWrapper className='SignUpError'>
                          <ErrorSign>{errors.signature}</ErrorSign>
                        </ErrorSignWrapper>
                      </SignUpError>
                    )}

                    <DialogActions className='justifyEnd'>
                      <DialogAction className='w30'>
                        <ButtonOutline
                          onClick={() => history.push('/byoc-onboarding/start')}
                          type='button'
                        >
                          Back
                        </ButtonOutline>
                      </DialogAction>
                      <DialogAction className='w30'>
                        <ButtonPrimary
                          onClick={handleSubmitWithScroll}
                          type='submit'
                          disabled={loading}
                        >
                          <span>Continue</span>
                        </ButtonPrimary>
                      </DialogAction>
                    </DialogActions>
                    {loading && (
                      <ByocOnboardingLoadingOverlay>
                        <Loading />
                      </ByocOnboardingLoadingOverlay>
                    )}
                  </SignUpContainer>
                </Form>
              );
            }}
          </Formik>
        </>
      )}
    </DashboardContent>
  );
};

export default SignUp;
